import { useEffect, useState } from 'react';

import { Flex, Grid } from '@chakra-ui/react';
import { EmployeeIdBadge, Icon, Select, SelectContent, SelectItem, SelectTrigger } from '@mesh.ai/rabbit';
import { L3_PATHS } from 'Deer/constants/routes';
import { useDispatch } from 'react-redux';
import { setSurveyCreationPresetLoadKey, useSurveyCreationData } from 'reducers/surveyCreationSlice';
import { LabelFieldWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/LabelFieldWrapper';
import { SectionWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/SectionWrapper';

import { Visibility } from 'assets/assetsV1/iconComponents';
import { Anonymous } from 'assets/assetsV1/iconComponents/Anonymous';

import { Button } from 'uiToolkitV1/Button';
import { GradientBGCard } from 'uiToolkitV1/GradientBGCard/GradientBGCard';
import { Radio } from 'uiToolkitV1/Radio';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

import {
  useGetSurveySelectedTemplate,
  useGetSurveyTemplateList,
  useUpdateSurveySelectedTemplate,
  useUpdateSurveySettings,
} from '../dataHook';
import { EMPLOYEE_PRIVACY_LABELS, EMPLOYEE_PRIVACY_VALUES, type SurveyTemplate } from '../types';
import { usePresetInitializationInfo } from '../usePresetInitializationInfo';
import { SurveySectionLoader } from './SurveyCreationLoader';

const PRIVACY_OPTIONS = [
  // {
  //   id: EMPLOYEE_PRIVACY_VALUES.ANONYMOUS,
  //   title: EMPLOYEE_PRIVACY_LABELS[EMPLOYEE_PRIVACY_VALUES.ANONYMOUS],
  //   description: 'No identifying information collected',
  //   icon: Anonymous,
  // },
  {
    id: EMPLOYEE_PRIVACY_VALUES.SEMI_ANONYMOUS,
    title: EMPLOYEE_PRIVACY_LABELS[EMPLOYEE_PRIVACY_VALUES.SEMI_ANONYMOUS],
    description: 'Function, Location and Subsidiary Information collected',
    icon: Anonymous,
  },
  {
    id: EMPLOYEE_PRIVACY_VALUES.NON_ANONYMOUS,
    title: EMPLOYEE_PRIVACY_LABELS[EMPLOYEE_PRIVACY_VALUES.NON_ANONYMOUS],
    description: 'Name and other employee details collected',
    icon: EmployeeIdBadge,
  },
];

const radioStyles = {
  // default styles override
  border: '1px solid',
  borderColor: '#CCCCCC !important',
  _before: { background: '#0D819F !important', height: '8px !important', width: '8px !important' },
  // checked styles override
  '&.radioChecked': { borderColor: '#0D819F !important' },
  // hover styles override
  _hover: {
    borderColor: '#0D819F !important',
    '&.radioChecked': { borderColor: '#0D819F !important' },
  },
  // focus styles override
  '&[data-focus]': { boxShadow: '0 0 0 3px rgba(13, 129, 159, 0.4) !important' },
};

const PreviewFormButton = ({ surveyId }: { surveyId: string }) => {
  const previewURL = L3_PATHS.company.engagement.previewSurvey.replace(':surveyId', surveyId);

  return (
    <Flex gap={3}>
      <Button
        as="a"
        href={previewURL}
        target="_blank"
        variant="outlined"
        StartIcon={Visibility}
        startIconProps={{ height: '20px', width: '20px' }}
        height="32px"
      >
        Preview Survey
      </Button>
    </Flex>
  );
};

type SurveyTypeSectionProps = {
  surveyId: string;
  defaultEmployeePrivacy: keyof typeof EMPLOYEE_PRIVACY_VALUES;
  isReadOnly: boolean;
};

export const SurveyTypeSection = ({ surveyId, defaultEmployeePrivacy, isReadOnly }: SurveyTypeSectionProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<SurveyTemplate | null>(null);
  const [employeePrivacy, setEmployeePrivacy] = useState<keyof typeof EMPLOYEE_PRIVACY_VALUES>(
    defaultEmployeePrivacy || EMPLOYEE_PRIVACY_VALUES.SEMI_ANONYMOUS
  );

  const { isPresetInitialization, presetInfo } = usePresetInitializationInfo();
  const { isHandledPresetTemplateLoad } = useSurveyCreationData();

  const dispatch = useDispatch();

  const {
    data: surveyTemplateList,
    isLoading: isSurveyTemplateListLoading,
    isError: isSurveyTemplateListError,
  } = useGetSurveyTemplateList();
  const { data: surveySelectedTemplate, isLoading: isSurveySelectedTemplateLoading } =
    useGetSurveySelectedTemplate(surveyId);
  const { mutate: updateSurveySelectedTemplate } = useUpdateSurveySelectedTemplate();
  const { mutate: updateEmployeePrivacy } = useUpdateSurveySettings(surveyId);

  // set default template
  useEffect(() => {
    // Skip if it's a preset initialization
    if (isPresetInitialization) return;

    // Skip if either data is loading or no template list available
    if (isSurveySelectedTemplateLoading || isSurveyTemplateListLoading || !surveyTemplateList?.length) return;

    // If no default template exists, update with first template from list
    if (!surveySelectedTemplate || !('entity' in surveySelectedTemplate)) {
      if (surveyTemplateList.length > 0) {
        updateSurveySelectedTemplate({ surveyId, templateId: surveyTemplateList[0].id });
        setSelectedTemplate(surveyTemplateList[0]);
      }
    } else if ('entity' in surveySelectedTemplate) {
      // Find the template in the list that matches the selected template ID
      const templateId = surveySelectedTemplate.entity.id;
      const foundTemplate = surveyTemplateList.find((template) => template.id === templateId);
      if (foundTemplate) setSelectedTemplate(foundTemplate);
      // Fallback to first template if the selected one isn't found
      else if (surveyTemplateList.length > 0) setSelectedTemplate(surveyTemplateList[0]);
    }
  }, [
    isSurveySelectedTemplateLoading,
    isSurveyTemplateListLoading,
    surveySelectedTemplate,
    surveyId,
    surveyTemplateList,
    updateSurveySelectedTemplate,
  ]);

  const handleTemplateChange = (id: string) => {
    const templateId = Number(id);
    if (templateId === selectedTemplate?.id) return;
    const template = surveyTemplateList.find((temp) => temp.id === templateId);
    if (!template) return;
    updateSurveySelectedTemplate({ surveyId, templateId });
    setSelectedTemplate(template);
  };

  // temporary FE handle survey preset code
  // handle preset data load
  useEffect(() => {
    if (isPresetInitialization && !isHandledPresetTemplateLoad && surveyTemplateList) {
      const { templateId } = presetInfo;

      handleTemplateChange(templateId as unknown as string);

      // temporary hack as this is all throwaway work on FE to support survey presets
      // Delay to put request is completed before removing the preset query param
      // Because as soon as query param is removed, selected template will GET will be requested
      setTimeout(() => {
        dispatch(setSurveyCreationPresetLoadKey({ isHandledPresetTemplateLoad: true }));
      }, 1000);
    }
  }, [surveyTemplateList]);

  const handlePrivacyChange = (id: keyof typeof EMPLOYEE_PRIVACY_VALUES) => {
    if (id === employeePrivacy) return;
    setEmployeePrivacy(id);
    updateEmployeePrivacy({ surveyId, updatedFormFields: { visibilityType: id } });
  };

  if (isSurveyTemplateListLoading || isSurveySelectedTemplateLoading) return <SurveySectionLoader />;

  if (isSurveyTemplateListError || !surveyTemplateList || !selectedTemplate) {
    return (
      <SectionWrapper
        title="Survey type"
        subTitle="Pick a template to add relevant survey questions"
        restrictAccess={isReadOnly}
      >
        <Flex justifyContent="center" p={4}>
          <TextLabel color="text.error">Failed to load survey templates. Please try again later.</TextLabel>
        </Flex>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Survey type"
      subTitle="Pick a template to  add relevant survey questions"
      rightComp={!isReadOnly && <PreviewFormButton surveyId={surveyId} />}
      restrictAccess={isReadOnly}
    >
      <LabelFieldWrapper label="Select template" gridTemplateColumns="320px 1fr" gridGap={0}>
        <GradientBGCard h="92px" w="400px" display="flex" flexDir="column" justifyContent="center" gap="4px">
          <TextBodyMedium bold whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
            {selectedTemplate.name}
          </TextBodyMedium>
          <Select onValueChange={handleTemplateChange} value={String(selectedTemplate?.id)}>
            <SelectTrigger style={{ width: '100%', padding: 0, height: 'auto' }}>
              <Flex gap="4px">
                <TextLabel bold>Q</TextLabel>
                <TextLabel>
                  {selectedTemplate.questionCount} {selectedTemplate.questionCount === 1 ? 'question' : 'questions'}
                </TextLabel>
              </Flex>
            </SelectTrigger>
            <SelectContent style={{ maxWidth: '100%', maxHeight: '400px', overflow: 'auto' }}>
              {surveyTemplateList.map(({ id, name }) => (
                <SelectItem key={id} value={String(id)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </GradientBGCard>
      </LabelFieldWrapper>
      <LabelFieldWrapper label="Employee privacy" gridTemplateColumns="320px 1fr" gridGap={0}>
        <Grid templateColumns="repeat(3, minmax(200px, 250px))" w="100%" gridGap="16px">
          {PRIVACY_OPTIONS.map(({ id, title, description, icon }) => (
            <GradientBGCard
              key={id}
              cursor="pointer"
              display="flex"
              flexDir="column"
              gap="8px"
              onClick={() => handlePrivacyChange(id)}
            >
              <Flex
                alignItems="center"
                justifyContent="space-between"
                bg="white"
                fontWeight="bold"
                h="32px"
                borderRadius="16px"
                pl="4px"
                pr="6px"
              >
                <Flex alignItems="center" gap="8px">
                  <Icon IconSVG={icon} style={{ color: '#666666', height: '20px', width: '20px' }} />
                  <TextLabel bold color="text.g2">
                    {title}
                  </TextLabel>
                </Flex>
                <Radio name={id} onChange={() => {}} isChecked={employeePrivacy === id} sx={radioStyles} />
              </Flex>
              <TextLabelSmall ml="30px" color="text.g2">
                {description}
              </TextLabelSmall>
            </GradientBGCard>
          ))}
        </Grid>
      </LabelFieldWrapper>
    </SectionWrapper>
  );
};
